import { InputOptionBox, Button } from '../../../../core/components/Index';
import { observer } from 'mobx-react';
import { Appl } from '../../../../core/Appl';
import ApplyViewModel from './ApplyViewModel';
import { useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';

const TemporaryAnnuity = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.TemporaryAnnuity, memberId!);
  }, []);

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Temporary Annuity</h1>
      <div className="text-black text-15px tracking--1percent leading-26px space-y-4">
        <p>
          The Temporary Annuity option is designed for SCERS members who retire before age 62 and are covered by Social Security. It provides an advance payment on your SCERS pension based on your estimated Social Security benefit at age 62. This temporary increase in your SCERS pension helps fill the income gap before Social Security begins but may come with significant long-term consequences.
        </p>
        <p>
          Please note: This option is only available to Legacy members (Miscellaneous Tiers 1–4 and Safety Tiers 1–3) and is not available to PEPRA members (Miscellaneous Tier 5 and Safety Tier 4).
        </p>
        <p className="font-bold">How It Works:</p>
        <ul className="list-disc pl-6">
          <li>SCERS increases your pension with an "advance" on your future Social Security benefit.</li>
          <li>
            Once you reach age 62, your SCERS pension is <b>automatically and permanently reduced</b>—whether or not you apply for Social Security.
          </li>
          <li>
            The reduction is based on actuarial calculations, taking into account your life expectancy and SCERS mortality tables.
          </li>
        </ul>
        <p className="font-bold">Key Risks:</p>
        <ul className="list-disc pl-6">
          <li>
            <b>Permanent Reduction:</b> After the temporary annuity period ends, your SCERS monthly retirement allowance, including cost of living adjustments, is reduced for the rest of your life.
          </li>
          <li>
            <b>Potential Financial Insecurity:</b> Even though you receive more income early on, the long-term reduction could leave you with less income later in life, when expenses may increase and financial stability is critical.
          </li>
          <li>
            <b>Life Expectancy Considerations:</b> This option may seem advantageous if you expect to benefit from a higher income before age 62, but the permanent reduction makes it risky, especially if you live longer than expected.
          </li>
        </ul>
        <p>
          For many members, the temporary benefit does not outweigh the significant reduction in lifetime income. It’s important to carefully consider your long-term financial needs before selecting this option, as it could result in a significant loss of retirement income over time.
        </p>
      </div>

      <h2 className="text-22px tracking--1percent text-black font-semibold mt-6">Temporary Annuity Selection</h2>
      <p className="text-black text-15px tracking--1percent leading-26px">
        Now that you have an understanding of the Temporary Annuity option and its long-term effects on your retirement benefits, please indicate below if you wish to opt in to pursue this option.
      </p>
      <div className="space-y-8">
        <div className="flex items-start">
          <InputOptionBox
            inputId="tempAnnuitySelectionNotWish"
            value={vm.model.temporaryAnnuity.tempAnnuitySelection}
            checked={vm.model.temporaryAnnuity.tempAnnuitySelection && vm.model.temporaryAnnuity.tempAnnuitySelection !== 'A' ? true : false}
            // onChange={(e: any) => e.checked ? vm.model.temporaryAnnuity.tempAnnuitySelection = "D" : vm.model.temporaryAnnuity.tempAnnuitySelection = "A"}
            onChange={(e: any) => e.checked ? vm.model.temporaryAnnuity.tempAnnuitySelection = "D" : vm.model.temporaryAnnuity.tempAnnuitySelection = undefined}
            className="mt-2"
          />
          <label
            htmlFor="tempAnnuitySelectionNotWish"
            className="ml-2 text-black text-15px tracking--1percent leading-26px cursor-pointer mt-2"
          >
            I do not wish to pursue the Temporary Annuity option, or I understand as a PEPRA member I am not eligible to select this option.
          </label>
        </div>

        <div className="flex items-start">
          <InputOptionBox
            inputId="tempAnnuitySelectionAccept"
            value={vm.model.temporaryAnnuity.tempAnnuitySelection}
            checked={vm.model.temporaryAnnuity.tempAnnuitySelection && vm.model.temporaryAnnuity.tempAnnuitySelection === 'A' ? true : false}
            // onChange={(e: any) => e.checked ? vm.model.temporaryAnnuity.tempAnnuitySelection = "A" : vm.model.temporaryAnnuity.tempAnnuitySelection = "D"}
            onChange={(e: any) => e.checked ? vm.model.temporaryAnnuity.tempAnnuitySelection = "A" : vm.model.temporaryAnnuity.tempAnnuitySelection = undefined}
            className="mt-[1px]"
          />
          <label
            htmlFor="tempAnnuitySelectionAccept"
            className="ml-2 text-black text-15px tracking--1percent leading-26px cursor-pointer"
          >
            I wish to pursue the Temporary Annuity option under Section 31810 of the Government Code and understand that my SCERS pension will be permanently reduced after I reach the Social Security eligibility age, regardless of whether I apply for Social Security. I understand that my retirement application will not be processed until I have been contacted and counseled by SCERS regarding the Temporary Annuity option.
          </label>
        </div>
      </div>
      <div className="flex mt-6 flex-wrap gap-2 sm:flex-nowrap">
        <Button
          title={`${vm.navigatedFromReviewScreen ? 'Save & Return to Review' : 'Save & Continue'}`}
          rounded
          onClick={async () => await vm.onTemporaryAnnuitySelectionStepAsync()}
          disabled={vm.model.temporaryAnnuity.tempAnnuitySelection === undefined || vm.model.temporaryAnnuity.tempAnnuitySelection === ''}
        />
        {vm.navigatedFromReviewScreen && <Button title="Cancel & Return to Review" severity='secondary' rounded
          onClick={async () => await vm.navigateToReviewStepAsync()}
        />}
      </div>
    </div>
  );
});

export default TemporaryAnnuity;
